/**
 * Reconnaissance des titres de section dans une liste de recette — ingrédients
 * (US-41) **et étapes** (US-76).
 *
 * ## Ce qu'US-76 y a changé, et ce qu'elle n'y a pas touché
 *
 * Rien de l'heuristique. `grouperLignesIngredients` s'appelle désormais
 * `grouperLignesEnSections` — il n'a jamais rien su des ingrédients, seulement
 * des lignes et d'un arbitre — et le second paramètre d'`estUnTitreDeSection`
 * s'appelle `estDuContenu` plutôt qu'`aQuantite`, parce qu'une quantité n'est
 * que l'une des deux façons de savoir qu'une ligne n'est pas un titre. Aucun
 * comportement ne bouge : un test de non-régression le tient.
 *
 * `step.group_label` connaissait exactement le sort décrit ci-dessous pour son
 * homologue : une colonne conçue en migration 001, documentée au §2 — « Phase.
 * Nullable. » —, affichée par la fiche, le PDF et le mode cuisine depuis US-41,
 * corrigeable à la main depuis US-41 aussi… et **écrite `null` en dur** par
 * `buildSteps` depuis US-01. La moitié ingrédients avait été faite, l'autre non.
 *
 * ## Le constat d'origine (US-41)
 *
 * `ingredient.group_label` existe depuis la migration 001, documenté au §2 avec
 * les exemples « Pour la pâte » et « Pour la garniture ». **Rien ne le
 * remplit** — ni l'import URL, ni l'import texte, ni la reprise CookBook. C'est
 * une colonne conçue puis oubliée, et son absence se voit sur toute recette à
 * deux préparations : tarte, mille-feuille, pâte à choux.
 *
 * L'analyse de la tarte aux fraises de Marmiton (9 août 2026) a montré où est
 * le problème : le JSON-LD ne porte aucun groupe — douze ingrédients à plat,
 * dix `HowToStep` sans un seul `HowToSection`. Les intitulés « pour la pâte
 * sablée : » et « pour la crème pâtissière : » n'existent que dans le HTML
 * affiché, et arrivent donc, quand ils arrivent, mêlés aux ingrédients.
 *
 * **La règle est volontairement étroite.** Elle ne reconnaît un titre que sur
 * deux formes explicites : commencer par « pour », ou se terminer par deux
 * points. Rien d'autre. Les intitulés nus — « Poolish », « Chapelure » — sont
 * très probablement des titres eux aussi, mais rien dans le texte ne les
 * distingue d'un ingrédient réel : « Toute la poolish » en est un, dans la même
 * recette. Les deviner reviendrait à **effacer un ingrédient**, ce qui est
 * exactement ce que le §3.2 interdit.
 *
 * Ce qui rend l'heuristique acceptable alors que deviner est proscrit ailleurs :
 * l'étape 4 du §3.4 garantit que tout import passe par l'écran d'édition avant
 * enregistrement. Rien ne s'écrit ici sans avoir été vu.
 */

/**
 * « Pour la pâte », « Pour le beurre manié », « Ingrédients pour le
 * craquelin ». L'article est facultatif : « Pour décorer » est un titre.
 */
const COMMENCE_PAR_POUR = /^(?:ingr[ée]dients?\s+)?pour\b/i;

/** « Pâte sablée : », « Crème pâtissière : ». Le deux-points fait le titre. */
const FINIT_PAR_DEUX_POINTS = /:\s*$/;

/**
 * Un titre ne porte jamais de quantité. Ce contrôle passe **avant** les motifs :
 * « 4 pour cent de sel » commence par un chiffre, donc n'est pas un titre, quoi
 * qu'en dise la suite de la ligne.
 */
const CONTIENT_UN_NOMBRE = /\d/;

/**
 * Cette ligne est-elle un titre de section plutôt qu'une ligne de contenu ?
 *
 * `estDuContenu` est **l'arbitre**, et il change avec la liste (US-76). Pour un
 * ingrédient, il vient du parseur (§3.2) : une ligne dont il a tiré une
 * quantité ou une unité est un ingrédient, sans discussion. Pour une étape,
 * c'est `contientUnVerbeDEtape` : une ligne qui porte un verbe d'instruction
 * est une étape, quoi que dise sa forme — sans quoi « Pour finir, mélangez
 * délicatement » deviendrait un titre, et l'instruction disparaîtrait.
 *
 * Dans les deux cas, le texte seul ne suffit pas à trancher : c'est l'arbitre
 * qui sait, et il est fourni par l'appelant plutôt que deviné ici.
 */
export function estUnTitreDeSection(ligne: string, estDuContenu = false): boolean {
  if (estDuContenu) return false;
  const propre = ligne.trim();
  if (propre.length === 0) return false;
  // Un titre reste court. Au-delà, c'est une phrase, donc probablement un
  // ingrédient replié par la règle du §3.2 (« sel, poivre, et une pincée de… »).
  if (propre.length > 60) return false;
  if (CONTIENT_UN_NOMBRE.test(propre)) return false;
  return COMMENCE_PAR_POUR.test(propre) || FINIT_PAR_DEUX_POINTS.test(propre);
}

/**
 * Forme affichable d'un titre : « Ingrédients pour la pâte : » devient « Pour
 * la pâte ». On retire le préfixe redondant et la ponctuation terminale, on met
 * la première lettre en capitale — et on ne touche à rien d'autre, le texte
 * source restant celui de la recette.
 */
export function normaliserTitreDeSection(ligne: string): string {
  const propre = ligne
    .trim()
    .replace(/^ingr[ée]dients?\s+/i, '')
    .replace(/\s*:\s*$/, '')
    .trim();
  return propre.length === 0 ? propre : propre.charAt(0).toUpperCase() + propre.slice(1);
}

export interface LigneGroupee {
  /** Ligne de contenu — ingrédient ou étape — telle quelle. Les titres n'y figurent pas. */
  ligne: string;
  /** Titre de la section en cours, ou `null` avant le premier titre. */
  groupe: string | null;
}

/**
 * Répartit une liste de lignes en lignes de contenu porteuses de leur groupe.
 *
 * Les titres **disparaissent** de la liste : ils deviennent l'étiquette des
 * lignes qui suivent, jusqu'au titre suivant. Rien n'est réordonné — l'ordre de
 * la recette est celui de la recette (§ hors périmètre d'US-41).
 *
 * `estDuContenu` est consulté par ligne, ce qui permet à l'appelant de brancher
 * l'arbitre réel — le parseur d'ingrédients ou le verbe d'étape — plutôt
 * qu'une seconde heuristique.
 */
export function grouperLignesEnSections(
  lignes: readonly string[],
  estDuContenu: (ligne: string) => boolean = () => false,
): LigneGroupee[] {
  const sortie: LigneGroupee[] = [];
  let groupe: string | null = null;

  for (const brute of lignes) {
    const ligne = brute.trim();
    if (ligne.length === 0) continue;
    if (estUnTitreDeSection(ligne, estDuContenu(ligne))) {
      groupe = normaliserTitreDeSection(ligne);
      continue;
    }
    sortie.push({ ligne, groupe });
  }

  return sortie;
}

// ---------------------------------------------------------------------------
// Conversion ligne ↔ titre de section, à l'édition (US-41)
// ---------------------------------------------------------------------------
//
// Le groupement n'est reconnu qu'à l'import, et seulement sur des formes
// explicites (« Pour la pâte », « Pâte sablée : »). Tout le reste — un intitulé
// nu comme « Poolish », une recette saisie à la main, une reconnaissance qui
// s'est trompée — se corrige ici, d'un geste, dans les deux sens.
//
// C'est la contrepartie assumée d'une heuristique volontairement étroite : on
// ne devine presque rien, donc il faut pouvoir rectifier sans effort. Le §3.4
// dit la même chose pour l'import : la machine propose, l'humain valide.

export interface LigneGroupable {
  group_label: string | null;
}

/**
 * Cette ligne ouvre-t-elle une section ? Vrai si elle porte un groupe que la
 * précédente n'avait pas — c'est là, et seulement là, que le titre s'affiche.
 */
export function ouvreUneSection(lignes: readonly LigneGroupable[], index: number): boolean {
  const ligne = lignes[index];
  if (ligne === undefined || ligne.group_label === null) return false;
  return index === 0 || lignes[index - 1]?.group_label !== ligne.group_label;
}

/**
 * Transforme la ligne d'index `index` en **titre de la section qui suit**.
 *
 * La ligne disparaît de la liste — un titre n'est pas un ingrédient — et son
 * texte devient l'étiquette des lignes suivantes, jusqu'à la fin de la section
 * où elle se trouvait. On ne déborde jamais sur la section d'après : le
 * groupement décrit la recette, il ne la réorganise pas.
 *
 * `texte` extrait le libellé de la ligne : le nom pour un ingrédient,
 * l'instruction pour une étape. La fonction ignore tout le reste, ce qui lui
 * permet de servir aux deux listes sans les connaître.
 */
export function transformerEnTitre<T extends LigneGroupable>(
  lignes: readonly T[],
  index: number,
  texte: (ligne: T) => string,
): T[] {
  const cible = lignes[index];
  if (cible === undefined) return [...lignes];

  const titre = texte(cible).trim();
  // Un titre vide ne dirait rien et rendrait la ligne irrécupérable.
  if (titre.length === 0) return [...lignes];

  const sectionDOrigine = cible.group_label;
  const sortie = lignes.filter((_, i) => i !== index);

  for (let i = index; i < sortie.length; i += 1) {
    const ligne = sortie[i];
    if (ligne === undefined || ligne.group_label !== sectionDOrigine) break;
    sortie[i] = { ...ligne, group_label: titre };
  }

  return sortie;
}

/**
 * Opération inverse : le titre qui ouvre la section de la ligne `index`
 * redevient une ligne ordinaire, insérée à sa place.
 *
 * Les lignes de la section retrouvent le groupe englobant — celui de la ligne
 * qui précédait le titre, `null` la plupart du temps. `construire` fabrique la
 * ligne à insérer à partir du texte du titre : c'est l'appelant qui sait ce
 * qu'est une ligne dans sa liste.
 */
export function transformerTitreEnLigne<T extends LigneGroupable>(
  lignes: readonly T[],
  index: number,
  construire: (texte: string, groupe: string | null) => T,
): T[] {
  const cible = lignes[index];
  if (cible === undefined || cible.group_label === null) return [...lignes];

  // Début réel de la section : le titre s'affiche là, quel que soit l'endroit
  // où l'on a cliqué à l'intérieur.
  let debut = index;
  while (debut > 0 && lignes[debut - 1]?.group_label === cible.group_label) debut -= 1;

  const titre = cible.group_label;
  const groupeEnglobant = debut === 0 ? null : (lignes[debut - 1]?.group_label ?? null);

  const sortie = [...lignes];
  for (let i = debut; i < sortie.length; i += 1) {
    const ligne = sortie[i];
    if (ligne === undefined || ligne.group_label !== titre) break;
    sortie[i] = { ...ligne, group_label: groupeEnglobant };
  }

  sortie.splice(debut, 0, construire(titre, groupeEnglobant));
  return sortie;
}
